import {ofType} from 'redux-observable';
import {combineLatest, EMPTY, type Observable, of, zip} from 'rxjs';
import {flatMap, take, tap} from 'rxjs/operators';

import {combineEpics} from 'web-app/util/redux-observable';
import {hasValue} from 'web-app/util/typescript';
import {updateQueryParams} from 'web-app/util/url';
import * as actionTypes from 'web-app/react/constants/action-types';
import {getOfflineValues, OfflineDraftKey} from 'web-app/react/observation-editor/offline-drafter';
import {setInitialHistoryStackLength} from 'web-app/react/navigation-v2/actions';
import {ObservationVariant} from 'web-app/util/graphql/api-types';

import * as Actions from './actions';
import {QueryParams} from './types';

/**
 * Since the observation editor has offline drafting which is encrypted with account secrets,
 * we have to wait until account is fetched before we can render it. This epic defers all calls to
 * show the modal by waiting for account to be fetched.
 */
const show = (action$): Observable<never> =>
    combineLatest(
        action$.pipe(ofType(Actions.show.type)),
        action$.pipe(ofType(actionTypes.FETCH_ACCOUNT_SUCCESS), take(1)),
    ).pipe(
        tap(([{payload}]: {0: ReturnType<typeof Actions.show.action>} & Array<unknown>) => {
            showModal(payload);
        }),
        flatMap(() => EMPTY),
    );

const preview = (action$): Observable<never> =>
    action$.pipe(
        ofType(Actions.preview.type),
        tap(({payload}: ReturnType<typeof Actions.show.action>) => {
            showModal(payload);
        }),
        flatMap(() => EMPTY),
    );

const showModal = (
    props: ReturnType<typeof Actions.show.action>['payload'] | ReturnType<typeof Actions.preview.action>['payload'],
) => {
    updateQueryParams(params => {
        switch (props.mode) {
            case 'new':
                params.set(QueryParams.ObservationEditorModalMode, props.mode);
                params.set(QueryParams.ObservationEditorModalVariant, props.variant);
                if (hasValue(props.childIds)) {
                    params.set(QueryParams.ObservationEditorModalChildIds, props.childIds.toString());
                }
                return params;
            case 'edit':
                params.set(QueryParams.ObservationEditorModalMode, props.mode);
                params.set(QueryParams.ObservationEditorModalObservationId, props.observationId);
                return params;
            case 'preview':
                params.set(QueryParams.ObservationEditorModalMode, 'new');
                params.set(QueryParams.ObservationEditorModalVariant, ObservationVariant.REGULAR_OBSERVATION);
                return params;
            default:
                return params;
        }
    });
};

/**
 * Epic for consolidating all the logic we use for prompting the user
 * that there's an unsaved observation in their local storage on login
 *
 * setInitialHistoryStackLength action doesn't actually do anything necessary
 * for the editor to work, but without waiting for it it doesn't not open
 */
const promptOfflineDraftOnLogin = action$ =>
    zip(
        action$.pipe(ofType(actionTypes.FETCH_ACCOUNT_SUCCESS)),
        action$.pipe(ofType(setInitialHistoryStackLength.type)),
    ).pipe(flatMap(() => openOfflineDraft()));

const promptOfflineDraft = action$ =>
    action$.pipe(
        ofType(Actions.promptOfflineDraft.type),
        flatMap(() => openOfflineDraft()),
    );

const openOfflineDraft = () => {
    const offlineObservation = getOfflineValues(OfflineDraftKey);
    if (!hasValue(offlineObservation)) {
        return EMPTY;
    }
    return of(
        Actions.show.action({
            mode: 'new',
            variant: offlineObservation.variant,
        }),
    );
};

export default combineEpics(show, promptOfflineDraft, promptOfflineDraftOnLogin, preview);
